import React, { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { Row, Col, Nav, Offcanvas, Button, Card } from "react-bootstrap";
import { useAuth } from "../../shared/auth/AuthProvider";
import {
  collection,
  doc,
  getCountFromServer,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../shared/libs/firebaseConfig";
import { AFFILIATE_COLLECTIONS } from "../../shared/utils/affiliateSchema";
import "../../shared/styles/pages/Account.css";

function normalizeRoles(role) {
  if (!role) return [];
  const arr = Array.isArray(role) ? role : [role];
  return arr.map((r) => String(r).trim().toLowerCase());
}
function hasRole(roles, ...targets) {
  const set = new Set(normalizeRoles(roles));
  return targets
    .map((t) => String(t).trim().toLowerCase())
    .some((t) => set.has(t));
}

function pruneEmptyGroups(groups) {
  return groups
    .map((g) => ({
      ...g,
      items: (g.items || []).filter(Boolean),
    }))
    .filter((g) => g.items.length > 0);
}

function buildMenuGroups(roles, isApprovedAffiliate = false) {
  const isCreatorOrAdmin = hasRole(roles, "creator", "admin");
  const isAdmin = hasRole(roles, "admin");

  const groups = [];

  // ผู้ใช้ทั่วไป (ไม่ต้องมีหัวข้อใหญ่)
  groups.push({
    title: null,
    items: [
      { to: "/account/profile", label: "โปรไฟล์" },
      { to: "/account/my-courses", label: "คอร์สเรียนของฉัน" },
      { to: "/account/my-course-purchases", label: "การซื้อคอร์สเรียน" },
      { to: "/account/my-reviews", label: "รีวิวของฉัน" },
      { to: "/account/my-answer-sheets", label: "กระดาษคำตอบของฉัน" },
      /* { to: "/account/security", label: "ความปลอดภัย" },
      { to: "/account/purchases", label: "การสั่งซื้อของฉัน" },
      { to: "/account/notifications", label: "การแจ้งเตือน" }, */
    ],
  });

  groups.push({
    title: "Affiliate",
    show: isApprovedAffiliate,
    items: [
      { to: "/account/affiliate/profile", label: "โปรไฟล์ Affiliate" },
      { to: "/account/affiliate/links", label: "ลิงก์ Affiliate" },
      { to: "/account/affiliate/earnings", label: "รายได้ Affiliate" },
    ],
  });

  // Creator
  groups.push({
    title: "Creator",
    show: isCreatorOrAdmin,
    items: [
      isCreatorOrAdmin && { to: "/account/creator/courses", label: "คอร์สเรียนของฉัน" },
      isCreatorOrAdmin && { to: "/account/creator/enrollments", label: "ผู้สมัคร/การขาย" },
      isCreatorOrAdmin && { to: "/account/creator/videos", label: "วิดีโอของฉัน" },
      // isCreatorOrAdmin && { to: "/account/creator/enrollments", label: "ผู้สมัครเรียน" },
      // isCreatorOrAdmin && { to: "/account/creator/ImageGuidelinePage", label: "แนวทางการใช้รูปภาพ" },
    ],
  });

  // Admin
  groups.push({
    title: "Admin",
    show: isCreatorOrAdmin,
    items: [
      isAdmin && { to: "/account/admin/courses", label: "คอร์สเรียนในระบบ" },
      isAdmin && { to: "/account/admin/enrollments", label: "ผู้สมัคร/การขายในระบบ" },
      isAdmin && { to: "/account/admin/videos", label: "วิดีโอในระบบ" },
      isAdmin && { to: "/account/admin/UserInformation", label: "ข้อมูลสมาชิก" },
      isCreatorOrAdmin && { to: "/account/admin/affiliate-applications", label: "คำขอ Affiliate" },
      isCreatorOrAdmin && { to: "/account/admin/affiliates", label: "จัดการ Affiliate" },
      isCreatorOrAdmin && { to: "/account/admin/affiliate-commissions", label: "ค่าคอมมิชชัน Affiliate" },
      isAdmin && { to: "/account/admin/access-control", label: "ตั้งค่าการเข้าถึงผู้ใช้งาน"},
      isAdmin && { to: "/account/admin/device-monitor", label: "ข้อมูลการเข้าใช้งาน"},
      isAdmin && {
        to: "/account/admin/image-moderation",
        label: "ตรวจรูปภาพ",
        dangerKey: "imageModeration",
      },
      roles === "admin" && { to: "/account/admin/course-reviews", label: "จัดการรีวิว" },
      roles === "admin" && { to: "/account/admin/pre-tests", label: "จัดการแบบทดสอบ" },
      isAdmin && { to: "/account/admin/ContactMessages", label: "การติดต่อ" },
    ],
  });

  const visible = groups.filter((g) => g.show === undefined || g.show === true);
  return pruneEmptyGroups(visible);
}

function MenuList({ groups, onItemClick, moderationAlertCount = 0 }) {
  return (
    <Nav className="flex-column">
      {groups.map((g, gi) => (
        <React.Fragment key={`${g.title || "base"}-${gi}`}>
          {g.title && (
            <div className="account-menu-heading px-3 pt-3 pb-2">
              {g.title}
            </div>
          )}

          {g.items.map((s) => (
            <Nav.Link
              key={s.to}
              as={NavLink}
              to={s.to}
              end
              className={`px-3 py-3 account-side-link ${
                s.dangerKey === "imageModeration" &&
                moderationAlertCount > 0
                  ? "account-side-link-danger"
                  : ""
              }`}
              onClick={onItemClick}
            >
              {s.label}

              {s.dangerKey === "imageModeration" &&
                moderationAlertCount > 0 && (
                  <span className="ms-2 text-danger fw-bold">
                    ({moderationAlertCount})
                  </span>
              )}
            </Nav.Link>
          ))}
        </React.Fragment>
      ))}
    </Nav>
  );
}

export default function AccountLayout() {
  const [showMenu, setShowMenu] = useState(false);
  const { user, userDoc, loading } = useAuth();
  const roles = userDoc?.role;
  const [affiliateStatus, setAffiliateStatus] = useState("");
  const isApprovedAffiliate = affiliateStatus === "approved";

  // ✅ Hooks ต้องอยู่ก่อน early return เสมอ
  const MENU_GROUPS = useMemo(
    () => buildMenuGroups(roles, isApprovedAffiliate),
    [roles, isApprovedAffiliate]
  );

  // ✅ จะ useMemo หรือไม่ก็ได้ แต่ถ้าใช้ ต้องอยู่ก่อน return
  const FLAT_SECTIONS = useMemo(
    () => MENU_GROUPS.flatMap((g) => g.items),
    [MENU_GROUPS]
  );

  const [moderationAlertCount, setModerationAlertCount] = useState(0);

  useEffect(() => {
    if (!user?.uid) {
      setAffiliateStatus("");
      return undefined;
    }

    const unsub = onSnapshot(
      doc(db, AFFILIATE_COLLECTIONS.affiliates, user.uid),
      (snap) => {
        setAffiliateStatus(snap.exists() ? String(snap.data()?.status || "") : "");
      },
      (err) => {
        console.error("load affiliate menu status error:", err);
        setAffiliateStatus("");
      }
    );

    return unsub;
  }, [user?.uid]);

  useEffect(() => {
    const loadModerationAlert = async () => {
      try {
        const q = query(
          collection(db, "imageModerationLogs"),
          where("status", "==", "rejected")
        );

        const snap = await getCountFromServer(q);
        setModerationAlertCount(snap.data().count || 0);
      } catch (e) {
        console.error(e);
      }
    };

    if (hasRole(roles, "admin")) {
      loadModerationAlert();
    }
  }, [roles]);

  // ✅ early returns หลัง hooks
  if (loading) {
    // ...สเกเลตัน...
    return null;
  }
  if (!user) return null;

  

  return (
    <div className="container account-container">
      {/* Mobile pills */}
      <div className="d-md-none account-mobile-sticky">
        <div className="account-mobile-bar">
          <h5 className="mb-0">บัญชีของฉัน</h5>
          {/* <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowMenu(true)}
          >
            เมนูบัญชี
          </Button> */}
        </div>

        <Nav variant="pills" className="nav-pills-scroll flex-nowrap overflow-auto">
          {FLAT_SECTIONS.map((s) => (
            <Nav.Link key={s.to} as={NavLink} to={s.to} end className="px-3 py-2">
              {s.label}
            </Nav.Link>
          ))}
        </Nav>
      </div>

      {/* Desktop sidebar */}
      <Row className="gy-4">
        <Col md={3} className="d-none d-md-block">
          <Card className="account-sidebar">
            <Card.Body className="p-0">
              <MenuList
                groups={MENU_GROUPS}
                moderationAlertCount={moderationAlertCount}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={9}>
          <div className="account-content card">
            <div className="card-body">
              <Outlet />
            </div>
          </div>
        </Col>
      </Row>

      {/* Offcanvas */}
      {/* <Offcanvas
        show={showMenu}
        onHide={() => setShowMenu(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>เมนูบัญชี</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <MenuList groups={MENU_GROUPS} onItemClick={() => setShowMenu(false)} />
        </Offcanvas.Body>
      </Offcanvas> */}
    </div>
  );
}


